import { useEffect, useRef, useCallback } from "react";
import { motion, useAnimation } from "framer-motion";
import { Pause, Play } from "lucide-react";

type Props = {
  duration: number;
  isActive: boolean;
  isPaused: boolean;
  onComplete: () => void;
  onRestart: () => void;
};

const CircularTimer = ({
  duration,
  isActive,
  isPaused,
  onComplete,
  onRestart,
}: Props) => {
  const controls = useAnimation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const radius = 18;
  const circumference = 2 * Math.PI * radius;

  const resetAnimation = useCallback(() => {
    controls.set({ strokeDashoffset: circumference });
  }, [controls, circumference]);

  const start = useCallback(() => {
    resetAnimation();
    controls.start({
      strokeDashoffset: 0,
      transition: { duration, ease: "linear" },
    });
    timeoutRef.current = setTimeout(() => {
      onComplete();
    }, duration * 1000);
  }, [resetAnimation, controls, duration, onComplete]);

  const stop = useCallback(() => {
    controls.stop();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [controls]);

  useEffect(() => {
    stop(); // always clear on slide load
    resetAnimation();
    if (isActive && !isPaused) {
      start();
    }

    return () => stop();
  }, [isActive, isPaused, resetAnimation, start, stop]);

  useEffect(() => {
    stop();
    if (isActive && !isPaused) {
      start();
    }
  }, [isActive, isPaused, start, stop]);

  return (
    <div
      className="absolute bottom-16 left-3 z-30 sm:top-4 sm:bottom-auto sm:left-4 md:left-6"
      style={{
        bottom: "calc(max(1rem, env(safe-area-inset-bottom)) + 3.75rem)",
      }}
    >
      <div
        className="group relative h-10 w-10 cursor-pointer rounded-full border text-[color:var(--text)] shadow-[0_18px_55px_rgba(0,0,0,0.28)] backdrop-blur-xl transition hover:bg-black/45 sm:h-12 sm:w-12"
        style={{
          borderColor: "var(--surface-border)",
          background: "color-mix(in srgb, black 30%, transparent)",
        }}
        onClick={onRestart}
      >
        <svg
          className="h-10 w-10 -rotate-90 transform sm:h-12 sm:w-12"
          viewBox="0 0 40 40"
        >
          <circle
            cx="20"
            cy="20"
            r={radius}
            fill="transparent"
            stroke="color-mix(in srgb, var(--text-soft) 40%, transparent)"
            strokeWidth="4"
          />
          <motion.circle
            cx="20"
            cy="20"
            r={radius}
            fill="transparent"
            stroke="var(--accent)"
            strokeWidth="4"
            strokeDasharray={circumference}
            strokeDashoffset={circumference}
            animate={controls}
          />
        </svg>

        {/* Play/Pause icon centered */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          key={isPaused ? "play" : "pause"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {isPaused ? <Play size={16} /> : <Pause size={16} />}
        </motion.div>
      </div>
    </div>
  );
};

export default CircularTimer;
